'use client';

import React from 'react';
import { useRouter } from 'next/navigation';
import { Phone, Clock, Lock } from 'lucide-react';

interface PendingApprovalButtonProps {
  isCollapsed?: boolean;
}

/**
 * PendingApprovalButton - Sidebar item shown during AWAITING_SETUP
 *
 * Displays as a prominent button in the sidebar below Teams navigation
 * to inform users messaging is locked. Directs them to register a phone
 * number and complete 10DLC to unlock full platform features.
 */
export default function PendingApprovalButton({ isCollapsed = false }: PendingApprovalButtonProps) {
  const router = useRouter();

  if (isCollapsed) {
    // Collapsed view - icon with tooltip
    return (
      <div className="relative group px-3">
        <button
          onClick={() => router.push('/10dlc')}
          className="w-full flex items-center justify-center py-3 rounded-lg bg-gray-100 hover:bg-gray-200 transition-all shadow-sm"
          title="Messaging Locked"
        >
          <Phone className="h-4.5 w-4.5 text-gray-700" />
        </button>

        {/* Tooltip */}
        <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 hidden group-hover:block z-50 pointer-events-none">
          <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-lg">
            Messaging Locked
          </div>
        </div>
      </div>
    );
  }

  // Expanded view - modern professional card
  return (
    <div className="px-3 py-2">
      <div className="rounded-xl bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center h-7 w-7 rounded-lg bg-gray-100">
              <Lock className="h-4 w-4 text-gray-700" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900">
              Messaging Locked
            </h3>
          </div>
        </div>

        <p className="text-xs text-gray-600 leading-relaxed mb-3">
          Unlock full platform features by registering a phone number and completing 10DLC. We handle all the paperwork, we just need some agency info.
        </p>

        {/* Status indicator */}
        <div className="flex items-center gap-2 mb-3 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
          <Clock className="h-3.5 w-3.5 flex-shrink-0" />
          <span>Typically 4-10 business days</span>
        </div>

        {/* Action button */}
        <button
          onClick={() => router.push('/10dlc')}
          className="w-full flex items-center justify-center gap-1.5 rounded-lg bg-accent-blue text-white px-3 py-2.5 text-xs font-medium hover:brightness-110 transition-all shadow-sm whitespace-nowrap"
        >
          <Phone className="h-3.5 w-3.5" />
          Register Phone Number
        </button>
      </div>
    </div>
  );
}
